import { grey } from "@mui/material/colors";
import {
  alpha,
  createTheme,
  SimplePaletteColorOptions,
  ThemeOptions,
} from "@mui/material/styles";
import { CommonColors } from "@mui/material/styles/createPalette";

interface TextColors {
  primary: string;
  secondary: string | undefined;
  disabled: string;
  hint: string;
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    small: true;
    "active-label": true;
  }
}

declare module "@mui/material/Toolbar" {
  interface ToolbarPropsVariantOverrides {
    small: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    square: true;
    "text-white": true;
  }
}

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    defaultBackground: true;
  }
}

declare module "@mui/material/Skeleton" {
  interface SkeletonPropsVariantOverrides {
    chip: true;
    "text-invert": true;
  }
}

declare module "@mui/material/Avatar" {
  interface AvatarPropsVariantOverrides {
    "circular-hover": true;
  }
}

declare module "@mui/material/Fab" {
  interface FabPropsVariantOverrides {
    tiny: true;
  }
}

export const theme = (() => {
  const { _env_ } = window;

  const sansFont =
    "Avenir LT Std, avenir next, avenir,-apple-system, BlinkMacSystemFont, segoe ui, helvetica neue, helvetica, Ubuntu, roboto, noto, arial, sans-serif";
  const defaultTheme = createTheme({
    palette: { mode: "dark" },
    typography: {
      fontFamily: sansFont,
    },
  });
  const borderRadius: number = 0;
  const commonColors: CommonColors = {
    black: "#000",
    white: "#fff",
  };
  const primary: SimplePaletteColorOptions = {
    main: _env_.REACT_APP_ACCENT_COLOR || "#32a2db",
  };
  const themeColors: SimplePaletteColorOptions = {
    main: "#292929",
  };
  const secondary: SimplePaletteColorOptions = {
    main: "#f68121",
  };
  const success: SimplePaletteColorOptions = {
    main: "rgb(75 ,161 ,68)",
  };
  const error: SimplePaletteColorOptions = {
    main: "#ff9292",
  };
  const warning: SimplePaletteColorOptions = {
    light: "rgb(183 ,171 ,31)",
    main: "rgb(150 ,140 ,25)",
    dark: "rgb(108 ,101 ,18)",
    contrastText: commonColors.white,
  };
  const text: TextColors = {
    primary: commonColors.white,
    secondary: grey[200],
    disabled: "rgba(204, 204, 204, 1)",
    hint: "rgba(0, 0, 0, 0.38)",
  };

  const overrides: ThemeOptions = {
    palette: {
      mode: "dark",
      primary,
      secondary,
      text,
      success,
      warning,
      error,
      background: {
        paper: commonColors.black,
        default: themeColors.main,
      },
      common: commonColors,
    },
    typography: {
      fontFamily: sansFont,
      h5: {
        textTransform: "uppercase",
      },
      subtitle2: {
        textTransform: "uppercase",
        color: commonColors.white,
        fontWeight: "bolder",
      },
    },
    shape: {
      borderRadius: borderRadius,
    },
    components: {
      MuiCssBaseline: {},
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: themeColors.main,
          },
        },
        variants: [
          {
            props: { variant: "chip" },
            style: {
              borderRadius: defaultTheme.spacing(2),
              height: defaultTheme.spacing(4),
              minWidth: defaultTheme.spacing(8),
            },
          },
          {
            props: { variant: "text-invert" },
            style: {
              backgroundColor: alpha(commonColors.white, 0.2),
            },
          },
        ],
      },
      MuiTextField: {
        variants: [
          {
            props: { variant: "filled" },
            style: {
              backgroundColor: commonColors.black,
              borderBottomWidth: 0,
            },
          },
        ],
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: commonColors.white,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: commonColors.black,
            ":before": {
              borderBottomWidth: 0,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 24,
            backgroundColor: commonColors.black,
            padding: defaultTheme.spacing(1, 2),
            fontWeight: "normal",
          },
        },
      },
      MuiChip: {
        styleOverrides: { root: { color: commonColors.white } },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "unset",
            backgroundColor: themeColors.main,
            color: themeColors.contrastText,
          },
        },
      },
      MuiTypography: {
        defaultProps: { color: commonColors.white },
        variants: [
          {
            props: { variant: "small" },
            style: {
              fontSize: "0.65rem",
              fontWeight: "lighter",
              color: commonColors.white,
            },
          },
          {
            props: { variant: "active-label" },
            style: {
              textTransform: "uppercase",
              fontSize: "0.8125rem",
              color: success.main,
            },
          },
        ],
      },
      MuiToolbar: {
        variants: [
          {
            props: { variant: "small" },
            style: {
              minHeight: defaultTheme.spacing(4),
              maxHeight: defaultTheme.spacing(4),
              zIndex: defaultTheme.zIndex.appBar + 101,
              backgroundColor: themeColors.main,
            },
          },
        ],
      },
      MuiAvatar: {
        variants: [
          {
            props: { variant: "circular-hover" },
            style: {
              backgroundColor: "unset",
              "&:hover": {
                backgroundColor: alpha(commonColors.white, 0.08),
              },
            },
          },
        ],
      },
      MuiFab: {
        variants: [
          {
            props: { variant: "tiny" },
            style: {
              minWidth: defaultTheme.spacing(2),
              minHeight: defaultTheme.spacing(2),
              width: defaultTheme.spacing(2),
              height: defaultTheme.spacing(2),
              "& > .MuiSvgIcon-root": {
                fontSize: "1em",
              },
            },
          },
        ],
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "square" },
            style: {
              color: primary.main,
              minWidth: defaultTheme.spacing(8),
              minHeight: defaultTheme.spacing(8),
              "& > .MuiSvgIcon-root": { color: commonColors.white },
              "&.active": { backgroundColor: primary.main },
            },
          },
          {
            props: { variant: "text-white" },
            style: {
              color: commonColors.white,
              "& > .MuiSvgIcon-root": { color: commonColors.white },
              "&.active": { backgroundColor: commonColors.white },
              "&:hover": { backgroundColor: alpha(commonColors.white, 0.08) },
            },
          },
        ],
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: commonColors.white,
            "&.Mui-selected": { color: commonColors.white },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            color: commonColors.white,
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: { color: commonColors.white },
        },
        variants: [
          {
            props: { variant: "defaultBackground" },
            style: {
              backgroundColor: themeColors.main,
            },
          },
        ],
      },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 700 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: "#292929",
            color: commonColors.white,
          },
        },
      },
    },
  };

  return createTheme(defaultTheme, overrides);
})();
